import { Component } from 'react';

/**
 * Scoped Error Boundary for all /admin routes.
 *
 * Why: an unhandled render error anywhere in the admin tree used to bubble
 * to the app-wide ErrorBoundary, replacing the ENTIRE app with the
 * "ZenScroll had trouble loading. Please close and reopen the app." screen
 * (the reported iOS failure).  This boundary contains the blast radius to
 * the admin viewport: the rest of the app, navigation, and session stay
 * alive, and the admin gets an inline Retry.
 *
 * Errors are logged with the `[ADMIN ERROR]` prefix (route, platform,
 * build, message, trimmed stacks — never tokens or PII) so they're
 * greppable in Xcode Console / Safari Web Inspector / adb logcat.
 */
export default class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const platform = (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web';
    const route = (typeof window !== 'undefined' && window.location?.pathname) || 'unknown';
    // eslint-disable-next-line no-console
    console.error('[ADMIN ERROR] Admin page crashed:', {
      route,
      platform,
      build: process.env.REACT_APP_BUILD_ID || process.env.NODE_ENV,
      name: error?.name,
      message: error?.message,
      stack: (error?.stack || '').split('\n').slice(0, 4).join(' | '),
      componentStack: (info?.componentStack || '').split('\n').slice(0, 6).join(' | '),
    });
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[ADMIN ERROR] full error:', error, info);
    }
  }

  handleRetry = () => {
    this.setState({ error: null });
    setTimeout(() => this.forceUpdate(), 0);
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        data-testid="admin-error-fallback"
        className="min-h-[70dvh] w-full flex items-center justify-center bg-[#0A1514] text-white px-6"
        style={{ fontFamily: "'Manrope', sans-serif" }}
      >
        <div className="max-w-sm w-full text-center bg-[#101F1D]/60 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
          <h2 className="text-2xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            Admin couldn&apos;t load
          </h2>
          <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">
            Something went wrong loading this admin section. The rest of
            ZenScroll is still working.
          </p>
          <div className="mt-6 flex flex-col gap-3">
            <button
              type="button"
              onClick={this.handleRetry}
              data-testid="admin-error-retry"
              className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full font-semibold bg-[#1C4749] text-white hover:bg-[#2A6F72] transition-colors"
            >
              Retry
            </button>
            <a
              href="/zenflow"
              data-testid="admin-error-back-to-feed"
              className="w-full inline-flex items-center justify-center px-6 py-3 rounded-full font-medium border border-white/15 text-white hover:bg-white/5 transition-colors"
            >
              Back to feed
            </a>
          </div>
        </div>
      </div>
    );
  }
}
